import { FirebaseApp } from 'firebase/app';
import {
  getAuth,
  Auth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  User,
  updateProfile,
  sendPasswordResetEmail,
  UserCredential,
  getIdToken,
  browserLocalPersistence,
  getIdTokenResult,
  AuthError,
  IdTokenResult
} from 'firebase/auth';
import { config, isServiceConfigured } from '@/shared/config/environment';
import { UserProfile } from '@/shared/types';
import {
  getSignInErrorMessage,
  getSignUpErrorMessage,
  getGoogleSignInErrorMessage,
  getPasswordResetErrorMessage
} from '@/shared/services/auth/errorHandlers';
import { firebaseUserToProfile } from '@/shared/services/auth/profileConverter';
import app from '@/shared/services/firebase';

/**
 * Firebase Auth Service
 * Moved to shared layer as multiple features (admin, billing, auth) need it
 */
class FirebaseAuthService {
  private app: FirebaseApp | null = null;
  private auth: Auth | null = null;
  private authStateListeners: Array<(user: User | null) => void> = [];
  private currentFirebaseUser: User | null = null;  // Track current auth state
  private isAuthInitialized: boolean = false;  // Track if Firebase has completed initial auth check

  constructor() {
    this.initializeFirebase();
  }

  private initializeFirebase() {
    if (!isServiceConfigured('firebase')) {
      console.warn('Firebase is not configured. Authentication features will be limited.');
      return;
    }

    try {
      // Use the already initialized Firebase app from the shared services
      this.app = app;
      this.auth = getAuth(this.app);

      // Set auth persistence
      this.auth.setPersistence(browserLocalPersistence);

      // Listen to auth state changes
      onAuthStateChanged(this.auth, (user) => {
        this.notifyAuthStateChange(user);
      });

      // Mark that we're now listening for auth changes
      // The first callback from onAuthStateChanged will mark initialization as complete

      console.log('✅ Firebase Auth initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize Firebase Auth:', error);
    }
  }

  // Public initialize method
  async initialize(): Promise<void> {
    // Firebase is already initialized in the shared services
    // No additional initialization needed
    return Promise.resolve();
  }

  // Get current auth instance
  getAuth(): Auth | null {
    return this.auth;
  }

  // Get current user
  getCurrentUser(): UserProfile | null {
    const user = this.auth?.currentUser;
    return user ? firebaseUserToProfile(user) : null;
  }

  // Sign in with email and password
  async signIn(email: string, password: string): Promise<UserProfile> {
    if (!this.auth) {
      throw new Error('Firebase Auth is not initialized');
    }

    try {
      const result = await signInWithEmailAndPassword(this.auth, email, password);
      console.log('✅ User signed in successfully');
      return firebaseUserToProfile(result.user);
    } catch (error) {
      const authError = error as AuthError;
      console.error('❌ Sign in error:', authError.code);
      throw new Error(getSignInErrorMessage(authError.code));
    }
  }

  // Sign up with email and password
  async signUp(email: string, password: string, displayName: string): Promise<UserProfile> {
    if (!this.auth) {
      throw new Error('Firebase Auth is not initialized');
    }

    try {
      const result = await createUserWithEmailAndPassword(this.auth, email, password);

      // Update user profile with display name
      if (result.user) {
        await updateProfile(result.user, { displayName });
      }

      console.log('✅ User account created successfully');
      return firebaseUserToProfile(result.user);
    } catch (error) {
      const authError = error as AuthError;
      console.error('❌ Sign up error:', authError.code);
      throw new Error(getSignUpErrorMessage(authError.code));
    }
  }

  // Sign in with Google
  async signInWithGoogle(): Promise<UserProfile> {
    if (!this.auth) {
      throw new Error('Firebase Auth is not initialized');
    }

    try {
      const provider = new GoogleAuthProvider();
      // Add scopes if needed
      provider.addScope('email');
      provider.addScope('profile');

      const result = await signInWithPopup(this.auth, provider);
      console.log('✅ Google sign in successful');
      return firebaseUserToProfile(result.user);
    } catch (error) {
      const authError = error as AuthError;
      console.error('❌ Google sign in error:', authError.code);
      throw new Error(getGoogleSignInErrorMessage(authError.code));
    }
  }

  // Sign out
  async signOut(): Promise<void> {
    if (!this.auth) {
      return;
    }

    try {
      await signOut(this.auth);
      console.log('✅ User signed out successfully');
    } catch (error) {
      console.error('❌ Sign out error:', error);
      throw new Error('Failed to sign out. Please try again.');
    }
  }

  // Reset password
  async resetPassword(email: string): Promise<void> {
    if (!this.auth) {
      throw new Error('Firebase Auth is not initialized');
    }

    try {
      await sendPasswordResetEmail(this.auth, email);
      console.log('✅ Password reset email sent');
    } catch (error) {
      const authError = error as AuthError;
      console.error('❌ Password reset error:', authError.code);
      throw new Error(getPasswordResetErrorMessage(authError.code));
    }
  }

  // Listen to auth state changes
  onAuthStateChanged(callback: (user: User | null) => void): () => void {
    // IMMEDIATELY call the callback with the current auth state if already known
    // This fixes the race condition where ProtectedRoute mounts after login,
    // but the Firebase listener has already fired.
    //
    // CRITICAL FIX: We check if we have a known auth state in TWO ways:
    // 1. If isAuthInitialized is true (Firebase has completed initial check)
    // 2. If we have a currentFirebaseUser from the Auth instance directly
    //
    // This ensures that even if the listener hasn't fired yet, we can still
    // provide the current auth state immediately.
    if (this.isAuthInitialized) {
      // Auth state is confirmed initialized, use cached value
      try {
        callback(this.currentFirebaseUser);
      } catch (error) {
        console.error('Error in immediate auth state callback:', error);
      }
    } else if (this.auth?.currentUser) {
      // Firebase Auth has a user but our listener hasn't fired yet
      // This can happen when the service just initialized and Firebase restored session from storage
      // We can trust the currentUser from the Auth instance
      try {
        this.currentFirebaseUser = this.auth.currentUser;
        callback(this.auth.currentUser);
      } catch (error) {
        console.error('Error in immediate auth state callback from currentUser:', error);
      }
    }
    // If neither condition is true, we wait for the Firebase listener to fire

    // Add listener to array for future updates
    this.authStateListeners.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.authStateListeners.indexOf(callback);
      if (index > -1) {
        this.authStateListeners.splice(index, 1);
      }
    };
  }

  // Notify all listeners of auth state change
  private notifyAuthStateChange(user: User | null): void {
    // Store current auth state
    this.currentFirebaseUser = user;
    // Mark that Firebase has completed its initial auth check
    this.isAuthInitialized = true;

    // Notify all listeners
    this.authStateListeners.forEach(listener => {
      try {
        listener(user);
      } catch (error) {
        console.error('Error in auth state listener:', error);
      }
    });
  }

  // Convert Firebase User to UserProfile format
  firebaseUserToProfile(user: User, additionalData?: Partial<UserProfile>): UserProfile {
    return firebaseUserToProfile(user, additionalData);
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.auth?.currentUser;
  }

  // Get user ID token with custom claims for Supabase
  async getIdToken(forceRefresh = false): Promise<string | null> {
    if (!this.auth?.currentUser) {
      return null;
    }

    try {
      return await this.auth.currentUser.getIdToken(forceRefresh);
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  }

  // Get current Firebase user UID
  getUid(): string | null {
    return this.currentFirebaseUser?.uid || null;
  }

  // Get ID token result with claims
  async getIdTokenResult(forceRefresh = false): Promise<IdTokenResult | null> {
    if (!this.auth?.currentUser) {
      return null;
    }

    try {
      const result = await this.auth.currentUser.getIdTokenResult(forceRefresh);
      return result;
    } catch (error) {
      console.error('Error getting ID token result:', error);
      return null;
    }
  }

  // Check if user has required custom claims for Supabase
  async hasSupabaseClaims(): Promise<boolean> {
    if (!this.auth?.currentUser) {
      return false;
    }

    try {
      const tokenResult = await this.getIdTokenResult();
      const claims = tokenResult?.claims || {};

      return claims.role === 'authenticated';
    } catch (error) {
      console.error('Error checking Supabase claims:', error);
      return false;
    }
  }

  // Update user after successful authentication to set claims
  private async setCustomClaimsForUser(user: User): Promise<void> {
    // This would typically be done via a Cloud Function
    // For now, we'll note that claims need to be set on the backend
    console.log('Note: Custom claims should be set via Cloud Function or admin SDK');
  }

  // Update user profile
  async updateProfile(profileData: Partial<UserProfile>): Promise<UserProfile> {
    if (!this.auth?.currentUser) {
      throw new Error('No authenticated user');
    }

    // Update Firebase user profile if display name changed
    if (profileData.name && profileData.name !== this.auth.currentUser.displayName) {
      await updateProfile(this.auth.currentUser, { displayName: profileData.name });
    }

    // For now, return the updated profile
    // In a real app, you'd save this to your database
    const currentProfile = this.getCurrentUser();
    if (!currentProfile) throw new Error('No current user profile');

    return { ...currentProfile, ...profileData };
  }
}

// Create and export singleton instance
export const firebaseAuthService = new FirebaseAuthService();

// Export types for TypeScript
export type { User, UserCredential };
export { GoogleAuthProvider };
